import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { GroupOperation } from '@group/domain/GroupEntity/GroupOperation';
import type { GroupId } from 'balus-common';
import { toast } from 'react-hot-toast';
import type { GroupMember } from '@group/hooks/useGroupMembers';

type Props = {
  isOpen: boolean;
  selectedMemberData: GroupMember;
  onClose(): void;
  groupId: GroupId;
};

export const GroupMemberRemoveModal: React.FC<Props> = ({ isOpen, onClose, selectedMemberData, groupId }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleRemove = useCallback(async () => {
    const confirmMessage = [
      t('group.members.removeModal.confirmLine1', { name: selectedMemberData.name }),
      t('group.members.removeModal.confirmLine2'),
      t('group.members.removeModal.confirmLine3'),
    ].join('\n');

    if (!confirm(confirmMessage)) {
      onClose();
      return;
    }

    setLoading(true);

    try {
      await GroupOperation.removeMembers(groupId, [selectedMemberData.id]);
      toast.success(t('group.members.removeModal.successToast', { name: selectedMemberData.name }));
      onClose();
    } catch (err) {
      alert(t('group.members.removeModal.errorAlert'));
    }

    setLoading(false);
  }, [onClose, groupId, selectedMemberData, t]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="flex flex-col">
      {/* header */}
      <div className="mb-1 p-4 text-2xl font-bold">{t('group.members.removeModal.title')}</div>

      {/* content */}
      <div className="overflow-y-auto px-4 py-2" style={{ maxHeight: 'calc(85vh - 160px)' }}>
        {t('group.members.removeModal.body', { name: selectedMemberData.name })}
      </div>

      {/* footer */}
      <hr className="my-2 w-full" />
      <div className="flex justify-end px-8 py-4">
        <FlatButton onClick={onClose}>{t('common.action.cancel')}</FlatButton>
        <FlatButton onClick={handleRemove} color="brand" className="ml-2" loading={loading}>
          {t('group.members.removeModal.deleteButton')}
        </FlatButton>
      </div>
    </Modal>
  );
};
